import React, {useEffect, useRef, useState} from 'react';
import Map, {Layer, NavigationControl, Popup, Source} from 'react-map-gl';
import WebMercatorViewport from '@math.gl/web-mercator';
import Moment from 'moment';

const BaseMapSimple = ({features, hoveredEvent, onPinClick, onPinHover, onPinLeave, mapDrawTrigger, mapCenter, isDarkMode = false}) => {
    const mapRef = useRef();
    const [popupInfo, setPopupInfo] = useState(null);
    const [cursor, setCursor] = useState('grab');

    // Calculate bounds from features
    const getBounds = (features) => {
        if (!features?.features?.length) return {longitude: -100, latitude: 40, zoom: 3};

        const points = features.features.reduce((coords, feature) => {
            if (feature.geometry.type === 'Point') {
                coords.push(feature.geometry.coordinates);
            } else if (feature.geometry.type === 'LineString') {
                coords.push(...feature.geometry.coordinates);
            }
            return coords;
        }, []);

        const lngs = points.map(coord => coord[0]);
        const lats = points.map(coord => coord[1]);

        const cornersLngLat = [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)]
        ];

        const viewport = new WebMercatorViewport({
            width: window.innerWidth,
            height: window.innerHeight
        }).fitBounds(cornersLngLat, {
            padding: 50
        });

        return {
            longitude: viewport.longitude,
            latitude: viewport.latitude,
            zoom: viewport.zoom
        };
    };

    useEffect(() => {
        const icons = [
            {iconName: "star", iconSrc: "/icons/star.png"},
            {iconName: "homeFilled", iconSrc: "/icons/homeFilled.png"},
            {iconName: "person", iconSrc: "/icons/person_pin_circle_24dp_000_FILL0_wght400_GRAD0_opsz24.png"},
        ]
        if (mapRef.current) {
            icons.forEach(icon => {
                mapRef.current.loadImage(icon.iconSrc, (error, image) => {
                    console.log(`loading icon ${icon.iconSrc}`, error ? `ERROR: ${error}` : 'SUCCESS');
                    if (error) {
                        console.error(`Failed to load icon ${icon.iconSrc}:`, error);
                        return;
                    }
                    if (!mapRef.current.hasImage(icon.iconName)) {
                        // Use SDF for star icon (allows dynamic coloring) but not for homeFilled (use original color)
                        const useSdf = icon.iconName === "star";
                        mapRef.current.addImage(icon.iconName, image, {sdf: useSdf});
                        console.log(`Added icon ${icon.iconName} to map`);
                    }
                });
            })
        }

    }, [mapRef.current]);

    // Update popup and center map when hovered event changes
    useEffect(() => {
        if (hoveredEvent) {
            setPopupInfo({
                longitude: hoveredEvent.longitude ?? hoveredEvent.lon,
                latitude: hoveredEvent.latitude ?? hoveredEvent.lat,
                data: hoveredEvent
            });

            // Fly to the hovered location
            if (mapRef.current) {
                const zoom = features?.features?.length ? getBounds(features).zoom : 4;
                mapRef.current.getMap().flyTo({
                    center: [
                        hoveredEvent.longitude ?? hoveredEvent.lon,
                        hoveredEvent.latitude ?? hoveredEvent.lat
                    ],
                    zoom: zoom,
                    duration: 1000
                });
            }
        } else {
            setPopupInfo(null);
        }
    }, [hoveredEvent]);

    // Update map bounds when features change
    useEffect(() => {
        if (mapRef.current && features?.features?.length) {
            const bounds = getBounds(features);
            mapRef.current.getMap().flyTo({
                center: [bounds.longitude, bounds.latitude],
                zoom: bounds.zoom,
                duration: 1000
            });
        }
    }, [mapDrawTrigger]);

    // Center map on specific coordinates when mapCenter changes
    useEffect(() => {
        if (mapRef.current && mapCenter && mapCenter.longitude && mapCenter.latitude) {
            mapRef.current.getMap().flyTo({
                center: [mapCenter.longitude, mapCenter.latitude],
                zoom: 14, // Higher zoom level for site centering
                duration: 1000
            });
        }
    }, [mapCenter]);

    const onMouseEnter = () => {
        setCursor('pointer');
    };

    const onMouseLeave = () => {
        setCursor('grab');
    };

    const onClick = (event) => {
        if (!event.features?.length) return;

        const feature = event.features[0];
        const eventData = feature.properties.data;
        if (eventData) {
            // Handle different data types - some are already objects, others need parsing
            const data = typeof eventData === 'string' ? JSON.parse(eventData) : eventData;
            
            // Set popup info for building icons (sites)
            if (data.site_name) {
                setPopupInfo({
                    longitude: data.site_lon,
                    latitude: data.site_lat,
                    data: data
                });
            } else if (data.canvasser_name) {
                // Set popup info for person icons (humans)
                setPopupInfo({
                    longitude: data.longitude,
                    latitude: data.latitude,
                    data: data
                });
            } else {
                // Call the original onPinClick for other types
                onPinClick(data);
            }
        }
    };

    function renderPopup() {
        if (!popupInfo || !popupInfo.data) return null;

        // offices
        if (popupInfo.data.addressline1) {
            return <Popup
                longitude={popupInfo.data.lon}
                latitude={popupInfo.data.lat}
                anchor="bottom"
                onClose={() => setPopupInfo(null)}
                className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-lg p-4`}
            >
                <div className="text-sm">
                    <b><i>{popupInfo.data.name}</i></b>
                    <b>
                        {popupInfo.data.addressline1}, {popupInfo.data.addressline2},
                        {popupInfo.data.city}, {popupInfo.data.statecode}, {popupInfo.data.zip5}
                    </b>
                    <div>Total Checkins: {popupInfo.data.num_checkins}</div>
                    <div>Total Responses: {popupInfo.data.num_response}</div>
                </div>
            </Popup>
        }

        // Canvasses
        if (popupInfo.data.last_location_update_time) {
            return <Popup
                longitude={popupInfo.data.longitude}
                latitude={popupInfo.data.latitude}
                anchor="bottom"
                onClose={() => setPopupInfo(null)}
                className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-lg p-4`}
            >
                <div className="text-sm">
                    <b>
                        {popupInfo.data.canvasser_name}
                    </b>
                    <div>Last Location
                        Update: {Moment(popupInfo.data.last_location_update_time).format('LT')}</div>
                </div>
            </Popup>
        }

        // Events
        if (popupInfo.data.phone_time) {
            return <Popup
                longitude={popupInfo.longitude}
                latitude={popupInfo.latitude}
                anchor="bottom"
                onClose={() => setPopupInfo(null)}
                className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-lg p-4`}
            >
                <div className="text-sm">
                    <b>
                        {popupInfo.data.canvasser_firstname} {popupInfo.data.canvasser_lastname}
                    </b>
                    <div>Event: {popupInfo.data.event_type.replace(/_/g, ' ')}</div>
                    <div>Office: {popupInfo.data.office_id}</div>
                    <div>Time: {Moment(popupInfo.data.phone_time).format('LT')}</div>
                    <div>Location Source: {popupInfo.data.loc_source?.replaceAll('_', ' ')}</div>
                </div>
            </Popup>
        }

        // Sites (buildings)
        if (popupInfo.data.site_name) {
            return <Popup
                longitude={popupInfo.longitude}
                latitude={popupInfo.latitude}
                anchor="bottom"
                onClose={() => setPopupInfo(null)}
                className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-lg p-4`}
            >
                <div className="text-sm">
                    <b>
                        <i className="fas fa-building" style={{marginRight: '8px'}}></i>
                        {popupInfo.data.site_name}
                    </b>
                    <div>Site ID: {popupInfo.data.site_id}</div>
                </div>
            </Popup>
        }

        // Humans (people)
        if (popupInfo.data.canvasser_name) {
            return <Popup
                longitude={popupInfo.longitude}
                latitude={popupInfo.latitude}
                anchor="bottom"
                onClose={() => setPopupInfo(null)}
                className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-lg p-4`}
            >
                <div className="text-sm">
                    <b>
                        <i className="fas fa-user" style={{marginRight: '8px'}}></i>
                        {popupInfo.data.canvasser_name}
                    </b>
                    <div>Event: {popupInfo.data.event_type?.replace(/_/g, ' ')}</div>
                    <div>Time: {Moment(popupInfo.data.phone_time || popupInfo.data.event_time).format('LT')}</div>
                </div>
            </Popup>
        }

        return null;
    }

    // offices
    const starLayer = {
        id: 'star-points',
        type: 'symbol',
        layout: {
            'icon-image': [
                'case',
                ['==', ['get', 'iconName'], 'star'], 'star', // Built-in Maki 'star' icon
                ''
            ],
            'icon-size': 1,
            'icon-allow-overlap': true
        },
        paint: {
            'icon-opacity': 0.8
        },
        filter: ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'iconName'], 'star']]
    };

    // sites (buildings)
    const buildingLayer = {
        id: 'building-points',
        type: 'symbol',
        layout: {
            'icon-image': [
                'case',
                ['==', ['get', 'iconName'], 'homeFilled'], 'homeFilled', // Custom building icon
                ''
            ],
            'icon-size': .9,
            'icon-allow-overlap': true
        },
        paint: {
            'icon-opacity': 1
        },
        filter: ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'iconName'], 'homeFilled']]
    };

    // humans (people)
    const personLayer = {
        id: 'person-points',
        type: 'symbol',
        layout: {
            'icon-image': [
                'case',
                ['==', ['get', 'iconName'], 'person'], 'person', // Person icon
                ''
            ],
            'icon-size': 1.2,
            'icon-allow-overlap': true
        },
        paint: {
            'icon-opacity': 1.0
        },
        filter: ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'iconName'], 'person']]
    };

// Normal circle markers
    const pointLayer = {
        id: 'points',
        type: 'circle',
        paint: {
            'circle-radius': 6,
            'circle-color': ['get', 'groupColor'],
            'circle-opacity': 0.8,
            'circle-stroke-width': 1,
            'circle-stroke-color': isDarkMode ? '#000' : '#fff'
        },
        filter: ['all', ['==', ['geometry-type'], 'Point'], ['!=', ['get', 'iconName'], 'star'], ['!=', ['get', 'iconName'], 'homeFilled'], ['!=', ['get', 'iconName'], 'person']]
    };

    const regularLineLayer = {
        id: "regular-segments",
        source: "location-symbols",
        type: 'line',
        paint: {
            'line-width': 3,
            'line-opacity': 0.8,
            'line-gradient': isDarkMode ? [
                'interpolate',
                ['linear'],
                ['line-progress'],
                0, "#5a7a5a",
                0.15, "#8aa88a",
                0.3, "#c5d5c5",
                0.45, "#e2d23d",
                0.6, "#f5cb9a",
                0.75, "#f0aa7a",
                1, "#da7a4c",
            ] : [
                'interpolate',
                ['linear'],
                ['line-progress'],
                0, "#3d5941",
                0.15, "#778868",
                0.3, "#b5b991",
                0.45, "#d2b61d",
                0.6, "#edbb8a",
                0.75, "#de8a5a",
                1, "#ca562c",
            ],
            'line-color': [
                'case',
                ['has', 'useGradient'],
                ['get', 'color'],
                isDarkMode ? '#6bb6ff' : '#4a90e2'
            ]
        },
        filter: [
            'all',
            ['==', ['geometry-type'], 'LineString'],
            ['==', ['get', 'isGap'], false]
        ],
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        }
    };

    const gapLineLayer = {
        id: "gap-segments",
        source: "location-symbols",
        type: 'line',
        paint: {
            'line-width': 3,
            'line-opacity': 1,
            'line-color': isDarkMode ? '#8a6f6f' : '#593f3f',
            'line-dasharray': [1, 1] // Optional: makes gap segments dashed
        },
        filter: [
            'all',
            ['==', ['geometry-type'], 'LineString'],
            ['==', ['get', 'isGap'], true]
        ],
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        }
    };


    return (
        <Map
            ref={mapRef}
            mapboxAccessToken="pk.eyJ1IjoiYWxleGdpbHNvbiIsImEiOiJjbG4wdDJ4eGMxcGcwMmlxdXNwa3A1ZXc5In0.dINhFz-zHChgqP0LCjgK9w"
            initialViewState={{
                longitude: -100,
                latitude: 40,
                zoom: 3
            }}
            style={{width: '100%', height: '100%'}}
            mapStyle={isDarkMode ? "mapbox://styles/mapbox/dark-v10" : "mapbox://styles/mapbox/light-v10"}
            cursor={cursor}
            interactiveLayerIds={['points', 'building-points', 'person-points']}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
        >
            <NavigationControl position="bottom-left" showCompass={false}/>

            <Source
                type="geojson"
                data={features}
                lineMetrics={true}
            >
                <Layer {...buildingLayer} />
                <Layer {...personLayer} />
                <Layer {...gapLineLayer} />
                <Layer {...regularLineLayer} />
                <Layer {...starLayer} />
                <Layer {...pointLayer} />
            </Source>

            {renderPopup()}
        </Map>
    );
};

export default BaseMapSimple;
